/**
 * EndWithIntentionScreen — Buddha moment: echoes user's intentions, offers return or end.
 *
 * UI-INV-005: Leaf component — props only, no engine imports.
 * screen-integration-008
 */
import styles from "./EndWithIntentionScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function formatIntentEcho(intentLabels: string[]): string {
  if (intentLabels.length === 0) return "You came here to sit.";
  if (intentLabels.length === 1) return `You came here to sit with your ${intentLabels[0]}.`;
  return `You came here to sit with your ${intentLabels[0]}, and with ${intentLabels[1]}.`;
}

type Props = {
  intentLabels: string[];
  elapsedSeconds: number;
  nextSessionName?: string;
  nextSessionMinutes?: number | null;
  whisperLine?: string;
  onReturn: () => void;
  onEndAnyway: () => void;
};

export function EndWithIntentionScreen({
  intentLabels,
  elapsedSeconds,
  nextSessionMinutes,
  whisperLine,
  onReturn,
  onEndAnyway,
}: Props) {
  const intentEcho = formatIntentEcho(intentLabels);
  const elapsedLabel = formatElapsed(elapsedSeconds);

  return (
    <div className={styles.screen} data-testid={TEST_IDS.END_INTENTION_SCREEN}>
      {/* MetaStrip */}
      <div className={styles.meta}>
        <button className={styles.returnBtn} onClick={onReturn} type="button">
          ← keep practising
        </button>
      </div>

      <div className={styles.content}>
        {/* Hairline instead of circle */}
        <div className={styles.hairline} data-testid={TEST_IDS.END_INTENTION_HAIRLINE} />

        {/* Intent echo */}
        <p className={styles.intentEcho} data-testid={TEST_IDS.END_INTENTION_ECHO}>
          {intentEcho}
        </p>

        {/* Whisper */}
        {whisperLine && (
          <p className={styles.whisper}>{whisperLine}</p>
        )}

        {/* Time card */}
        <div className={styles.timeCard} data-testid={TEST_IDS.END_INTENTION_TIME_CARD}>
          {nextSessionMinutes != null
            ? `You've been practising for ${elapsedLabel} · the next bell rings in ${nextSessionMinutes} min.`
            : `You've been practising for ${elapsedLabel}.`}
        </div>

        {/* Actions */}
        <PrimaryAction onClick={onReturn} testId={TEST_IDS.END_INTENTION_CONTINUE_CTA}>Return to practice</PrimaryAction>
        <SecondaryAction onClick={onEndAnyway} testId={TEST_IDS.END_INTENTION_END_CTA}>End the retreat anyway</SecondaryAction>
        <p className={styles.footer}>Ending now is okay. We'll save what you've done.</p>
      </div>
    </div>
  );
}
